/**
 * ECS (Entity Component System) minimale e type-safe.
 *
 * Le entità sono semplici id numerici; i componenti sono dati puri registrati
 * per tipo; i sistemi sono funzioni/oggetti che iterano sulle query.
 * Nessun riferimento al DOM o a PixiJS: il modulo è testabile in Node e
 * riutilizzabile lato server nella Fase 2 (multiplayer autoritativo).
 */

export type Entity = number;

export interface ComponentType<T> {
  readonly key: string;
  /** Solo per inferenza di tipo, mai valorizzato a runtime. */
  readonly _marker?: T;
}

export function defineComponent<T>(key: string): ComponentType<T> {
  return { key };
}

export interface System {
  readonly name: string;
  update(world: World, dt: number): void;
}

export class World {
  private nextId: Entity = 1;
  private alive = new Set<Entity>();
  private stores = new Map<string, Map<Entity, unknown>>();
  private systems: System[] = [];

  createEntity(): Entity {
    const id = this.nextId++;
    this.alive.add(id);
    return id;
  }

  destroyEntity(entity: Entity): void {
    this.alive.delete(entity);
    for (const store of this.stores.values()) store.delete(entity);
  }

  isAlive(entity: Entity): boolean {
    return this.alive.has(entity);
  }

  private storeFor(key: string): Map<Entity, unknown> {
    let store = this.stores.get(key);
    if (!store) {
      store = new Map();
      this.stores.set(key, store);
    }
    return store;
  }

  add<T>(entity: Entity, type: ComponentType<T>, value: T): T {
    this.storeFor(type.key).set(entity, value);
    return value;
  }

  get<T>(entity: Entity, type: ComponentType<T>): T | undefined {
    return this.storeFor(type.key).get(entity) as T | undefined;
  }

  /** Come get(), ma lancia se il componente manca: utile nei sistemi dove è garantito. */
  require<T>(entity: Entity, type: ComponentType<T>): T {
    const value = this.get(entity, type);
    if (value === undefined) {
      throw new Error(`Entità ${entity}: componente mancante "${type.key}"`);
    }
    return value;
  }

  has<T>(entity: Entity, type: ComponentType<T>): boolean {
    return this.storeFor(type.key).has(entity);
  }

  remove<T>(entity: Entity, type: ComponentType<T>): void {
    this.storeFor(type.key).delete(entity);
  }

  /** Tutte le entità vive che possiedono tutti i componenti indicati. */
  query(...types: ComponentType<unknown>[]): Entity[] {
    if (types.length === 0) return [...this.alive];
    const [first, ...rest] = types;
    const result: Entity[] = [];
    const firstStore = this.storeFor(first!.key);
    for (const entity of firstStore.keys()) {
      if (!this.alive.has(entity)) continue;
      if (rest.every((t) => this.storeFor(t.key).has(entity))) result.push(entity);
    }
    return result;
  }

  addSystem(system: System): void {
    this.systems.push(system);
  }

  update(dt: number): void {
    for (const system of this.systems) system.update(this, dt);
  }
}
